// Mini-CRM popup — modular extraction of source-of-truth
// /var/www/kitchenos/portal-standalone.html lines 17578 (overlay shell),
// 17614 (`openMiniCrm`), 17669 (`closeMiniCrm`), 17710–17717 (ESC handler),
// 17722–17767 (`[data-customer-name]` capture-phase delegation).
//
// Behavioural parity:
//   • Same DOM shape — `#mini-crm-overlay` > `#mini-crm-card` with `.show`
//     toggled on the overlay.
//   • Same animations (`mcOverlayIn` / `mcCardIn` — in mini-crm.css).
//   • ESC + backdrop-click both close the popup.
//   • Found-state markup: avatar (initials), name + hot dot, stage pill,
//     town/postcode, Project / Value / Phone / Address / Next action /
//     Last touch rows, lead-score bar, Call / Message / Full record row.
//   • Not-found markup: name + initials in head, "No CRM record found"
//     subtitle + body, [Close] / [Open CRM] actions.
//   • £-formatted value uses `en-GB` locale, `--` fallback for empties,
//     hot dot renders as a red ● next to the name.
//   • Score colour ramp: green ≥70, amber ≥40, red <40 — gradient ends in
//     `#ffb84d` exactly like source.
//
// Departures (deliberate):
//   • No globals on `window` — the source bolts `openMiniCrm` onto
//     `window`; the modular version exports module functions and one
//     `installMiniCrmDelegation()` to wire the document listener once.
//   • No inline `onclick=""` — every interactive element wires its handler
//     in JS so CSP-friendly bundles can ship.
//   • The address row's `Edit` button (`data-addr-edit-cid`) is omitted
//     because `kosEditCustomerAddress` is a future extraction.

import './mini-crm.css';
import { maskPhone, maskEmail } from '../../mask';

// ── Public types ─────────────────────────────────────────────────────────────

export interface MiniCrmCustomer {
  id: string;
  name: string;
  hot?: boolean;
  stage?: string;
  town?: string;
  postcode?: string;
  street?: string;
  project?: string;
  value?: number;
  phone?: string;
  email?: string;
  nextAction?: string;
  lastTouch?: string;
  score?: number;
  /** Server-side identifier used when navigating to the full CRM page. */
  focusId?: string;
}

export interface MiniCrmOptions {
  /** Pre-fetched customer record. If absent, the popup shows the not-found
   *  state with the given name. */
  customer?: MiniCrmCustomer;
  /** Used when `customer` is absent — the not-found header still renders
   *  this name. */
  name?: string;
  /** Called when "Full record" / "Open CRM" is clicked. The popup closes
   *  automatically BEFORE the callback fires. */
  onViewFullRecord?: (customer: MiniCrmCustomer | null) => void;
  /** Required call handler — defraud-sacred. The shared component
   *  refuses to fall back to a `tel:` href; callers MUST wire this to
   *  the Twilio-masked dial endpoint (POST /api/portal/calls/dial)
   *  so the customer's phone number never leaves the platform. */
  onCall?: (customer: MiniCrmCustomer) => void;
  /** Required message handler — defraud-sacred. Must navigate the
   *  retailer into the platform Messages thread for this customer.
   *  Never `mailto:` (defeats on-platform comms tracking). */
  onMessage?: (customer: MiniCrmCustomer) => void;
}

// ── Internals ────────────────────────────────────────────────────────────────

const OVERLAY_ID = 'mini-crm-overlay';
const CARD_ID    = 'mini-crm-card';

interface OverlayRefs {
  overlay: HTMLElement;
  card: HTMLElement;
}

let overlayRefs: OverlayRefs | null = null;
let keydownInstalled = false;
let currentOptions: MiniCrmOptions | null = null;

function initials(name: string | undefined | null): string {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  const a = ((parts[0] ?? '')[0] ?? '').toUpperCase();
  const b = ((parts[1] ?? '')[0] ?? '').toUpperCase();
  return (a + b) || '?';
}

function escHtml(s: unknown): string {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return map[c] ?? c;
  });
}

function fmtMoney(n: unknown): string {
  const v = typeof n === 'number' && Number.isFinite(n)
    ? n
    : parseFloat(String(n ?? '')) || 0;
  return '£' + v.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

function ensureOverlay(): OverlayRefs {
  if (overlayRefs && overlayRefs.overlay.isConnected) return overlayRefs;

  // Reuse an existing overlay if a previous version somehow stuck around
  // in the DOM (defensive — guards against double-install during HMR).
  const existing = document.getElementById(OVERLAY_ID);
  if (existing instanceof HTMLElement) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;

  const card = document.createElement('div');
  card.id = CARD_ID;

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // Backdrop click closes — match source line 17578.
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeMiniCrm();
  });

  overlayRefs = { overlay, card };
  installKeydown();
  return overlayRefs;
}

function installKeydown(): void {
  if (keydownInstalled) return;
  keydownInstalled = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!overlayRefs) return;
    if (overlayRefs.overlay.classList.contains('show')) {
      e.preventDefault();
      closeMiniCrm();
    }
  });
}

// ── SVG icons (verbatim from source line 17658–17664) ────────────────────────

const ICON_PHONE =
  '<svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
const ICON_MESSAGE =
  '<svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
const ICON_CHEVRON =
  '<svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>';

// ── Rendering ────────────────────────────────────────────────────────────────

function renderNotFound(card: HTMLElement, name: string): void {
  card.innerHTML =
    '<div class="mc-head">' +
      '<div class="mc-avatar">' + escHtml(initials(name)) + '</div>' +
      '<div style="flex:1;min-width:0">' +
        '<div class="mc-name">' + escHtml(name || 'Unknown') + '</div>' +
        '<div class="mc-sub">No CRM record found</div>' +
      '</div>' +
      '<button class="mc-x" type="button" data-mc-action="close" title="Close">&times;</button>' +
    '</div>' +
    '<div class="mc-body" style="padding:24px 20px;color:#888;font-size:12px;text-align:center">' +
      "This contact isn't in your CRM yet.<br/>Open Customers to add them." +
    '</div>' +
    '<div class="mc-actions">' +
      '<button class="mc-btn" type="button" data-mc-action="close">Close</button>' +
      '<button class="mc-btn mc-btn-primary" type="button" style="grid-column:span 2" data-mc-action="full-record">Open CRM</button>' +
    '</div>';
}

function renderFound(card: HTMLElement, c: MiniCrmCustomer): void {
  const score = Math.max(0, Math.min(100, Number.parseInt(String(c.score ?? 0), 10) || 0));
  const scoreCol = score >= 70 ? '#22c55e' : score >= 40 ? '#e58200' : '#ef4444';

  // DEFRAUD-SACRED: customer phone is masked in the retailer-facing UI
  // ([[feedback-contact-masking]]). The unmasked value is never written
  // into the DOM, not even in a `data-*` attribute or title — the Call
  // button hits the Twilio-masked endpoint server-side using the
  // customer id, not the number.
  const phoneMasked = maskPhone(c.phone);
  const hasPhone = c.phone != null && String(c.phone).trim() !== '';

  const townLine = c.town
    ? '<span>· ' + escHtml(c.town) + (c.postcode ? ' ' + escHtml(c.postcode) : '') + '</span>'
    : '';

  const addressParts = [c.street, c.town, c.postcode]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  const addressVal = addressParts.length
    ? escHtml(addressParts.join(', '))
    : '<span style="color:#666">--</span>';

  const callBtn = hasPhone
    ? '<button class="mc-btn" type="button" data-mc-action="call">' + ICON_PHONE + 'Call</button>'
    : '<button class="mc-btn" type="button" disabled>' + ICON_PHONE + 'Call</button>';

  card.innerHTML =
    '<div class="mc-head">' +
      '<div class="mc-avatar">' + escHtml(initials(c.name)) + '</div>' +
      '<div style="flex:1;min-width:0">' +
        '<div class="mc-name">' + escHtml(c.name) +
          (c.hot ? ' <span style="color:#ef4444;font-size:13px">●</span>' : '') +
        '</div>' +
        '<div class="mc-sub">' +
          '<span class="mc-stage">' + escHtml(c.stage || 'Lead') + '</span>' +
          townLine +
        '</div>' +
      '</div>' +
      '<button class="mc-x" type="button" data-mc-action="close" title="Close">&times;</button>' +
    '</div>' +
    '<div class="mc-body">' +
      '<div class="mc-row"><span class="lbl">Project</span><span class="val">' + escHtml(c.project || '--') + '</span></div>' +
      '<div class="mc-row"><span class="lbl">Value</span><span class="val" style="color:#e58200">' + escHtml(fmtMoney(c.value)) + '</span></div>' +
      '<div class="mc-row"><span class="lbl">Phone</span><span class="val">' +
        (hasPhone ? '<span>' + escHtml(phoneMasked) + '</span>' : '--') +
      '</span></div>' +
      (c.email ? '<div class="mc-row"><span class="lbl">Email</span><span class="val">' + escHtml(maskEmail(c.email)) + '</span></div>' : '') +
      '<div class="mc-row"><span class="lbl">Address</span><span class="val">' + addressVal + '</span></div>' +
      '<div class="mc-row"><span class="lbl">Next action</span><span class="val">' + escHtml(c.nextAction || '--') + '</span></div>' +
      '<div class="mc-row"><span class="lbl">Last touch</span><span class="val">' + escHtml(c.lastTouch || '--') + '</span></div>' +
      '<div class="mc-score">' +
        '<div style="display:flex;justify-content:space-between;font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#888;font-weight:700">' +
          '<span>Lead score</span>' +
          '<span style="color:' + scoreCol + '">' + score + '/100</span>' +
        '</div>' +
        '<div class="mc-score-bar"><div class="mc-score-fill" style="width:' + score + '%;background:linear-gradient(90deg,' + scoreCol + ',#ffb84d)"></div></div>' +
      '</div>' +
    '</div>' +
    '<div class="mc-actions">' +
      callBtn +
      '<button class="mc-btn" type="button" data-mc-action="message">' + ICON_MESSAGE + 'Message</button>' +
      '<button class="mc-btn mc-btn-primary" type="button" data-mc-action="full-record">' + ICON_CHEVRON + 'Full record</button>' +
    '</div>';

  // NOTE: source-of-truth stashed the cleaned phone in card.dataset.mcPhone
  // for a `tel:` fallback. That breaks the defraud rule
  // ([[feedback-contact-masking]]) — the unmasked number must never
  // enter the DOM, not even in data-* or title attributes. The Call
  // button now relies on the consumer-provided `onCall` handler hitting
  // the Twilio-masked POST /api/portal/calls/dial endpoint using the
  // customer id; the number is resolved server-side and never leaves
  // the platform.
}

// ── Action wiring ────────────────────────────────────────────────────────────

function defaultCall(_c: MiniCrmCustomer): void {
  // DEFRAUD-SACRED: never `tel:` ([[feedback-contact-masking]]). When a
  // caller fails to wire `onCall`, log a warning rather than silently
  // breaking the defraud protection.
  console.warn(
    '[mini-crm] Call clicked but no onCall handler wired. ' +
    'Configure POST /api/portal/calls/dial in installMiniCrmDelegation.',
  );
}

function defaultMessage(_c: MiniCrmCustomer): void {
  // DEFRAUD-SACRED: never `mailto:`. The caller must navigate to the
  // platform messages thread.
  console.warn(
    '[mini-crm] Message clicked but no onMessage handler wired. ' +
    'Configure platform-messaging navigation in installMiniCrmDelegation.',
  );
}

function handleAction(action: string): void {
  if (!currentOptions || !overlayRefs) return;
  const customer = currentOptions.customer ?? null;

  if (action === 'close') {
    closeMiniCrm();
    return;
  }
  if (action === 'full-record') {
    const cb = currentOptions.onViewFullRecord;
    closeMiniCrm();
    cb?.(customer);
    return;
  }
  if (!customer) return;
  if (action === 'call') {
    (currentOptions.onCall ?? defaultCall)(customer);
    return;
  }
  if (action === 'message') {
    (currentOptions.onMessage ?? defaultMessage)(customer);
    return;
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export function openMiniCrm(opts: MiniCrmOptions): void {
  const { overlay, card } = ensureOverlay();
  currentOptions = opts;

  if (opts.customer) {
    renderFound(card, opts.customer);
  } else {
    renderNotFound(card, opts.name ?? '');
  }

  // Wire action buttons (delegated within the card).
  card.querySelectorAll<HTMLElement>('[data-mc-action]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const action = btn.getAttribute('data-mc-action');
      if (action) handleAction(action);
    });
  });

  overlay.classList.add('show');
}

export function closeMiniCrm(): void {
  if (!overlayRefs) return;
  overlayRefs.overlay.classList.remove('show');
  // Clear the card so stale handlers can't fire after close.
  overlayRefs.card.replaceChildren();
  currentOptions = null;
}

// ── Document-level delegation ────────────────────────────────────────────────

/** Optional anchor metadata harvested from the clicked DOM element. Lets
 *  callers handle the not-found state intelligently — e.g. the Submissions
 *  page sets `data-submission-id` so the shell can call the link-CRM
 *  endpoint to create a contact on first click. */
export interface MiniCrmAnchorMeta {
  submissionId?: string;
}

export interface MiniCrmDelegationOptions {
  /** Resolve a customer record from the clicked element's `data-customer-name`
   *  (and optional `data-customer-id`). MUST be synchronous so clicks feel
   *  instant — typically reads from an in-memory cache populated as pages
   *  load their data. Return `null` to show the not-found state. */
  lookup: (name: string, focusId?: string) => MiniCrmCustomer | null;
  /** Required — fires when the user clicks "Full record" / "Open CRM". */
  onViewFullRecord: (
    customer: MiniCrmCustomer | null,
    fallbackName?: string,
    meta?: MiniCrmAnchorMeta,
  ) => void;
  /** Optional Call / Message overrides — passed through to `openMiniCrm`. */
  onCall?: (customer: MiniCrmCustomer) => void;
  onMessage?: (customer: MiniCrmCustomer) => void;
}

/**
 * Install ONE document-level click handler that catches clicks on any
 * element carrying `data-customer-name="…"` and opens the mini-CRM popup.
 *
 * Capture phase + walking up via `closest` matches the source's behaviour
 * (line 17722) so the listener survives every page re-render. Returns a
 * teardown that removes the listener.
 */
export function installMiniCrmDelegation(opts: MiniCrmDelegationOptions): () => void {
  const handler = (e: MouseEvent): void => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest<HTMLElement>('[data-customer-name]');
    if (!el) return;

    // Respect explicit opt-outs: a page can mark an inner control with
    // data-customer-name-skip to keep its own handler.
    if ((e.target as HTMLElement).closest('[data-customer-name-skip]')) return;

    const name = el.getAttribute('data-customer-name')?.trim() ?? '';
    if (!name) return;

    e.preventDefault();
    e.stopPropagation();

    // Empty data-customer-id strings (now emitted by the Submissions page
    // when no CRM contact is linked yet) must be treated as "no id" so the
    // lookup falls back to name-matching rather than searching for "".
    const rawId = el.getAttribute('data-customer-id') ?? '';
    const focusId = rawId.trim() ? rawId : undefined;
    const submissionId = el.getAttribute('data-submission-id')?.trim() || undefined;
    const meta: MiniCrmAnchorMeta = {};
    if (submissionId) meta.submissionId = submissionId;

    const customer = opts.lookup(name, focusId);

    const popupOpts: MiniCrmOptions = {
      onViewFullRecord: (c) => {
        opts.onViewFullRecord(c, name, meta);
      },
    };
    if (customer) popupOpts.customer = customer;
    else popupOpts.name = name;
    if (opts.onCall) popupOpts.onCall = opts.onCall;
    if (opts.onMessage) popupOpts.onMessage = opts.onMessage;

    openMiniCrm(popupOpts);
  };

  // Capture phase so we beat any bubble-phase `stopPropagation` set by
  // page-level card handlers — same approach as source line 17767.
  document.addEventListener('click', handler, true);

  return () => {
    document.removeEventListener('click', handler, true);
  };
}
